/** 策略页（F-007/008/009 + F-011）。参考 v0 设计布局。 */
import { useMemo, useState } from 'react';
import { Zap, ChevronLeft, Play, Sliders, ChevronDown, ChevronUp } from 'lucide-react';
import { BUILTIN_STRATEGIES, findStrategy } from '../../strategy/registry';
import { useSetup } from '../store';
import { useLibrary } from '../library-store';
import { ParamForm } from '../components/ParamForm';
import { RuleEditor } from '../components/RuleEditor';
import { Section } from '../primitives';
import { cn } from '../cn';

const RULE_DESC = '通过条件—动作规则配置调度逻辑，支持优先级排序与冲突检测，未命中时降级为最近轿厢。';

interface Option {
  id: string;
  tag: string;
  name: string;
  desc: string;
}
const OPTIONS: Option[] = [
  ...BUILTIN_STRATEGIES.map((d) => ({ id: d.id, tag: d.id.toUpperCase().split('-')[0]!, name: d.name, desc: d.description })),
  { id: 'rule-engine', tag: 'RULE', name: '自定义规则引擎', desc: RULE_DESC },
];

export function StrategyPage() {
  const s = useSetup();
  const isRule = s.strategyId === 'rule-engine';
  const { strategies, addStrategy, removeStrategy } = useLibrary();
  const [stratName, setStratName] = useState('');
  const [open, setOpen] = useState(true);

  const desc = OPTIONS.find((o) => o.id === s.strategyId)?.desc ?? '';
  const paramFields = useMemo(
    () => (isRule ? undefined : findStrategy(s.strategyId)?.make().params),
    [isRule, s.strategyId],
  );

  return (
    <div className="flex flex-col gap-5 max-w-5xl mx-auto">
      <Section title="调度策略选择" icon={Zap}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {OPTIONS.map((o) => {
            const active = s.strategyId === o.id;
            return (
              <button
                key={o.id}
                onClick={() => s.patch({ strategyId: o.id, strategyParams: {} })}
                className={cn(
                  'flex flex-col gap-2 p-4 rounded-lg border text-left transition-all',
                  active ? 'border-primary/50 bg-primary/8 shadow-[0_0_16px_oklch(0.72_0.165_196/10%)]' : 'border-border bg-secondary/30 hover:border-border/60 hover:bg-secondary/50',
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono tracking-widest text-muted-foreground">{o.tag}</span>
                  <span className={cn('w-2.5 h-2.5 rounded-full border transition-colors', active ? 'bg-primary border-primary' : 'border-muted-foreground/40')} />
                </div>
                <span className="text-sm font-semibold text-foreground">{o.name}</span>
                <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">{o.desc}</p>
              </button>
            );
          })}
        </div>
      </Section>

      <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
        <p className="text-sm text-foreground/80 leading-relaxed">{desc}</p>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 hover:bg-secondary/30 transition-colors">
          <div className="flex items-center gap-2">
            <Sliders className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">{isRule ? '规则编辑（F-008）' : '策略参数（F-009）'}</span>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>
        {open && (
          <div className="px-5 pb-5 border-t border-border pt-4">
            {isRule ? (
              <div className="flex flex-col gap-4">
                <RuleEditor rules={s.rules} onSetRule={s.setRule} onAdd={s.addRule} onRemove={s.removeRule} />
                <div className="flex flex-col gap-2 pt-2 border-t border-border">
                  <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">策略库（F-011，刷新后仍在）</p>
                  <div className="flex gap-2 flex-wrap">
                    {strategies.map((st) => (
                      <div key={st.name} className="flex items-center gap-1">
                        <button onClick={() => useSetup.setState({ strategyId: 'rule-engine', rules: st.rules })} className="h-7 px-3 text-xs rounded border border-border text-foreground hover:border-primary/40 hover:text-primary transition-colors">{st.name}</button>
                        <button onClick={() => removeStrategy(st.name)} className="text-muted-foreground hover:text-destructive transition-colors">×</button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={stratName} onChange={(e) => setStratName(e.target.value)} placeholder="规则集名称" className="flex-1 h-7 bg-input/40 border border-border rounded px-2.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/60" />
                    <button onClick={() => { const n = stratName.trim(); if (n) { addStrategy(n, s.rules); setStratName(''); } }} className="h-7 px-3 text-xs rounded bg-primary/15 border border-primary/30 text-primary hover:bg-primary/25 transition-colors">保存</button>
                  </div>
                </div>
              </div>
            ) : paramFields && paramFields.length > 0 ? (
              <ParamForm fields={paramFields} value={s.strategyParams} onChange={(p) => s.patch({ strategyParams: p })} />
            ) : (
              <p className="text-xs text-muted-foreground">该策略无可调参数。</p>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button onClick={() => { window.location.hash = '#/config'; }} className="flex items-center gap-2 h-9 px-4 rounded text-sm text-muted-foreground border border-border hover:text-foreground hover:border-border/80 transition-colors">
          <ChevronLeft className="w-4 h-4" />上一步
        </button>
        <button onClick={() => { window.location.hash = '#/simulation'; }} className="flex items-center gap-2 h-9 px-5 rounded text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/85 transition-all shadow-[0_0_16px_oklch(0.72_0.165_196/20%)]">
          <Play className="w-4 h-4" />启动仿真
        </button>
      </div>
    </div>
  );
}
